import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  api,
  can,
  refusalReason,
  shortDate,
  tierLabel,
  type PolicyView,
  type ResolvedField,
} from '../lib/api'
import { ErrorBox, Loading } from './DataTable'
import { Status, Toggletip } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import { DateRuleNote, DateWarnNote } from './DateRuleNote'
import { checkNextReassessment, todayIndia, todayIso, useLeftFields, warningFor } from '../lib/dateRules'
import { personLabel, usePeopleNames } from '../lib/people'
import '../styles/sweep.css'


// The three places a value can come from. Naming the source is the whole point
// of the screen: a cadence with no provenance cannot answer why this vendor is
// on it, and "somebody set it once" is the answer that costs an audit.
const SOURCE_LABEL: Record<string, string> = {
  VENDOR_OVERRIDE: 'set here',
  TIER_DEFAULT: 'from the tier',
  SYSTEM_DEFAULT: 'system default',
}

const SOURCE_CLASS: Record<string, string> = {
  VENDOR_OVERRIDE: 'source-tag source-OVERRIDE',
  TIER_DEFAULT: 'source-tag source-TIER',
  SYSTEM_DEFAULT: 'source-tag source-SYSTEM',
}

const MODES: { value: string; label: string; detail: string }[] = [
  {
    value: 'FULL_CARRY_FORWARD',
    label: 'Carry every answer forward',
    detail: 'The vendor opens a reassessment filled in with what they said last time, and changes what has changed.',
  },
  {
    value: 'DELTA',
    label: 'Only the sections that changed',
    detail: 'Narrower and faster, and it only makes sense when the trigger names which sections it affects.',
  },
  {
    value: 'BLANK',
    label: 'Start from nothing',
    detail: 'Every answer given again from scratch. Honest, and the fastest way to make a vendor stop replying.',
  },
]

function fieldLabel(f: string): string {
  switch (f) {
    case 'cadenceMonths':
      return 'Cadence'
    case 'defaultMode':
      return 'Reassessment mode'
    case 'suppressionDays':
      return 'Suppression window'
    case 'eventTriggers':
      return 'Event triggers'
    case 'isPaused':
      return 'Paused'
    default:
      return f
  }
}

function renderValue(f: ResolvedField): string {
  if (f.value === null || f.value === undefined) return 'none'
  if (f.field === 'cadenceMonths') return f.value + ' months'
  if (f.field === 'suppressionDays') return f.value + ' days'
  if (typeof f.value === 'boolean') return f.value ? 'yes' : 'no'
  if (typeof f.value === 'object') return Object.keys(f.value).length === 0 ? 'none' : JSON.stringify(f.value)
  return String(f.value)
}

export function VendorPolicy({ vendorId }: { vendorId: string }) {
  const [view, setView] = useState<PolicyView | null>(null)
  const [error, setError] = useState<unknown>(null)

  const [open, setOpen] = useState(false)
  const [cadence, setCadence] = useState('')
  const [mode, setMode] = useState('FULL_CARRY_FORWARD')
  const [suppression, setSuppression] = useState('')
  const [paused, setPaused] = useState(false)
  const [pausedReason, setPausedReason] = useState('')
  // The next reassessment on a date a person chose. Empty means none, and
  // saving it empty clears one that was set.
  const [chosenOn, setChosenOn] = useState('')
  const [saving, setSaving] = useState(false)
  const names = usePeopleNames()
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  // D14 of the date rules, for a date being moved: after the last assessment
  // and not in the past, said on the field once the person leaves it.
  const left = useLeftFields()
  const chosenRule =
    chosenOn !== (view?.resolved.nextDueChosen?.on ?? '')
      ? checkNextReassessment(chosenOn, view?.vendor.lastAssessmentOn ?? '', todayIndia())
      : ''

  const load = useCallback(() => {
    api
      .reassessmentPolicy(vendorId)
      .then((v) => {
        setView(v)
        setError(null)
        setCadence(v.resolved.cadenceMonths === null ? '' : String(v.resolved.cadenceMonths))
        setMode(v.resolved.defaultMode)
        setSuppression(String(v.resolved.suppressionDays))
        setPaused(v.resolved.isPaused)
        setPausedReason(v.resolved.pausedReason ?? '')
        setChosenOn(v.resolved.nextDueChosen?.on ?? '')
      })
      .catch(setError)
  }, [vendorId])

  useEffect(load, [load])

  // The policy is saved whole, and the event triggers go back as they came:
  // this form does not edit them, and a PUT without them would clear them.
  function save() {
    if (chosenRule) {
      left.leave('chosenOn')
      return
    }
    setSaving(true)
    setSaveError(null)
    const ov = (view?.override ?? null) as { eventTriggers?: unknown } | null
    api
      .saveReassessmentPolicy(vendorId, {
        cadenceMonths: cadence.trim() === '' ? null : Number(cadence),
        defaultMode: mode,
        suppressionDays: suppression.trim() === '' ? null : Number(suppression),
        eventTriggers: ov?.eventTriggers,
        isPaused: paused,
        pausedReason: pausedReason,
        nextReassessmentOn: chosenOn,
      })
      .then((v) => {
        setView(v)
        setOpen(false)
      })
      .catch((err) => setSaveError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setSaving(false))
  }

  if (error != null) return <ErrorBox error={error} what="This vendor's policy" />
  if (!view) return <Loading what="the policy" />

  const r = view.resolved
  const chosen = r.nextDueChosen ?? null

  return (
    <div className="panel panel-flush">
      <div className="stat-strip">
        <div className="stat">
          <span className="metric">{r.cadenceMonths ?? 'On change'}</span>
          <span className="muted">{r.cadenceMonths ? 'months between assessments' : 'no timer'}</span>
        </div>
        <div className="stat">
          <span className="metric">{r.nextDueOn ? shortDate(r.nextDueOn) : 'not scheduled'}</span>
          <span className="muted sw-row">
            {chosen ? 'next due, set by ' + (personLabel(names, chosen.setBy) || 'a person') : 'next due'}{' '}
            <ReasonTip label="How the next date is worked out" text={r.nextDueBasis} />
          </span>
          <DateWarnNote id="vp-next-warn" message={warningFor(view.warnings, 'nextReassessmentOn')} />
        </div>
        <div className="stat">
          <span className="metric">{r.isPaused ? 'Paused' : 'Running'}</span>
          <span className="muted">{r.isPaused ? r.pausedReason || 'no reason recorded' : 'reassessment'}</span>
        </div>
      </div>

      <h3 className="sw-title">
        Where each value came from <ReasonTip label="How this policy applies" text={view.note} />
      </h3>
      <ul className="policy-provenance">
        {view.provenance.map((f) => (
          <li key={f.field}>
            <span>{fieldLabel(f.field)}</span>
            <span className={SOURCE_CLASS[f.source] ?? 'source-tag'}>{SOURCE_LABEL[f.source] ?? f.source}</span>
            <span className="sw-row">
              <span className="policy-value">{renderValue(f)}</span>
              <ReasonTip label={'Why ' + fieldLabel(f.field).toLowerCase() + ' is ' + renderValue(f)} text={f.detail} />
            </span>
          </li>
        ))}
      </ul>

      {view.tierDefault && (
        <p className="muted">
          {tierLabel(view.tierDefault.tier)} default:{' '}
          {view.tierDefault.cadenceMonths ? view.tierDefault.cadenceMonths + ' months' : 'on change only'}
          {view.tierDefault.questionnaireCode && (
            <>
              , questionnaire <span className="mono">{view.tierDefault.questionnaireCode}</span>
            </>
          )}
        </p>
      )}

      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {open ? 'Cancel' : 'Override for this vendor'}
      </button>

      {open && (
        <div className="fieldset">
          {saveError && <ErrorBox error={saveError} what="The policy" />}

          <div className="form-grid">
            <div className="field">
              <label htmlFor="vp-cadence">Cadence in months</label>
              <input
                id="vp-cadence"
                type="number"
                min={0}
                max={120}
                value={cadence}
                onChange={(e) => setCadence(e.target.value)}
                aria-describedby="vp-cadence-note"
              />
            </div>

            <div className="field">
              <label htmlFor="vp-next-on">Next reassessment on (optional)</label>
              <input
                id="vp-next-on"
                type="date"
                min={todayIso()}
                value={chosenOn}
                onChange={(e) => setChosenOn(e.target.value)}
                onBlur={() => left.leave('chosenOn')}
                aria-invalid={left.shown('chosenOn', chosenRule) ? true : undefined}
                aria-describedby={left.shown('chosenOn', chosenRule) ? 'vp-next-on-rule' : undefined}
              />
              <DateRuleNote id="vp-next-on-rule" message={left.shown('chosenOn', chosenRule)} />
            </div>

            <div className="field">
              <label htmlFor="vp-suppression">Suppression window in days</label>
              <input
                id="vp-suppression"
                type="number"
                min={0}
                max={365}
                value={suppression}
                onChange={(e) => setSuppression(e.target.value)}
                aria-describedby="vp-suppression-note"
              />
            </div>
          </div>

          <fieldset className="fieldset">
            <legend>What the vendor sees when reassessed</legend>
            {MODES.map((m) => (
              <label className="pickrow" key={m.value}>
                <input
                  type="radio"
                  name="mode"
                  value={m.value}
                  checked={mode === m.value}
                  onChange={() => setMode(m.value)}
                />
                <span className="sw-row">
                  <strong>{m.label}</strong>
                  <Toggletip label={'About ' + m.label.toLowerCase()}>{m.detail}</Toggletip>
                </span>
              </label>
            ))}
          </fieldset>

          <label className="chip-toggle">
            <input type="checkbox" checked={paused} onChange={(e) => setPaused(e.target.checked)} />
            Pause reassessment for this vendor
          </label>

          {paused && (
            <div className="field">
              <label htmlFor="vp-reason">Why it is paused</label>
              <input id="vp-reason" value={pausedReason} onChange={(e) => setPausedReason(e.target.value)} />
            </div>
          )}

          <div className="commit-bar">
            <button
              type="button"
              className="primary"
              onClick={save}
              disabled={saving || (paused && !pausedReason.trim()) || !can('assessment.plan')}
              title={can('assessment.plan') ? undefined : refusalReason('assessment.plan')}
              data-action="tprm.vendor,vendor-record.a23"
            >
              {saving ? 'Saving' : 'Save policy'}
            </button>
            {!can('assessment.plan') && (
              <span className="muted sw-row">
                <Status tone="neutral">Read only</Status>
                <ReasonTip label="Why you cannot change the policy" text={refusalReason('assessment.plan')} />
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
